/**
 * @fileoverview Report Template outline editor (MOR-556)
 *
 * Renders the AI-scanned section outline of a report template and lets the user
 * edit each section: title, guidance, content-type chips, and the
 * Generate / Attachment-only fill mode. Sections (and sub-sections) can be
 * reordered by drag-and-drop. Used inside Settings → Report Templates.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Badge, Button, Input, Spinner } from "@/ui/primitives";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertCircle,
  AlignLeft,
  BarChart3,
  ChevronDown,
  ChevronRight,
  GripVertical,
  ListTree,
  Loader2,
  Lock,
  MessageSquare,
  PanelLeftClose,
  Paperclip,
  PenLine,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Sparkles,
  Table,
  Trash2,
} from "lucide-react";
import {
  needsTemplateWordingFill,
  resolveAdherence,
  type SectionAdherence,
} from "~/convex/jobs/reporting/adherence";
import { toast } from "sonner";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import {
  TemplateChatPanel,
  type TemplateChatSelection,
} from "./TemplateChatPanel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays";
import { WordingRichEditor } from "./WordingRichEditor";
import {
  CHART_DIRECTIVE_RE,
  DATA_TAG_CHIP,
  FLEX_PHRASE_PILL,
  MERGE_FIELD_CHIP,
  extractQuotedPhrases,
  isDataTableDirective,
  looksLikeHtml,
  parseDirectiveColumns,
  renderMergeFieldHighlights,
} from "./wordingFormat";
import { extractDataTagIds } from "~/convex/jobs/reporting/dataSources";
import {
  buildContentsEntries,
  computeReorderedIds,
  groupSectionsIntoTree,
  type ContentsEntry,
  type ContentType,
  type FillMode,
  type Section,
} from "./reportSectionModel";

/** Hover explainer for a mode pill / content chip. */
function PillTip({ tip, children }: { tip: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-72 text-xs" side="bottom">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

const CONTENT_TYPE_TIPS: Record<ContentType, string> = {
  narrative: "This section may contain written prose.",
  table:
    "This section may include a table built from the matter's data. A [Table with fixed columns: …] directive in the wording controls exactly where and how.",
  chart:
    "This section may include a chart built from the matter's data. A [chart …] directive in the wording controls where it appears.",
  attachment: "This section references an attached annexure.",
};

const CONTENT_TYPE_OPTIONS: {
  value: ContentType;
  label: string;
  icon: typeof AlignLeft;
}[] = [
  { value: "narrative", label: "Narrative", icon: AlignLeft },
  { value: "table", label: "Table", icon: Table },
  { value: "chart", label: "Chart", icon: BarChart3 },
  { value: "attachment", label: "Attachment", icon: Paperclip },
];

/**
 * Label + icon per wording mode. Rendered as a quiet outline badge in a
 * consistent slot on every card — mode is told apart by icon and label, not
 * by loud colour (per design feedback, the tinted cards read off-brand).
 */
const ADHERENCE_META: Record<
  SectionAdherence,
  { label: string; icon: typeof Sparkles }
> = {
  flexible: { label: "AI drafted", icon: Sparkles },
  adaptable: { label: "Adaptable wording", icon: SlidersHorizontal },
  strict: { label: "Exact wording", icon: Lock },
};

/**
 * Textarea that grows to fit its content so guidance is never clipped, with a
 * backdrop layer highlighting `{{merge field}}` placeholders.
 */
function AutoTextarea({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  onTextSelect,
  flexPhrases,
  autoFocus,
  textareaRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  placeholder?: string;
  className?: string;
  /** Fires with the highlighted text when the user selects a passage. */
  onTextSelect?: (text: string) => void;
  /** Adaptation-note phrases to tint violet (adaptable sections). */
  flexPhrases?: string[];
  autoFocus?: boolean;
  /** Optional handle so a parent can insert text at the caret. */
  textareaRef?: MutableRefObject<HTMLTextAreaElement | null>;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      ref.current = node;
      if (textareaRef) textareaRef.current = node;
    },
    [textareaRef],
  );
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    resize();
  }, [value, resize]);
  // The measured height goes stale when the column width changes (assistant
  // panel opening, window resize) — re-measure so text is never clipped.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [resize]);
  useEffect(() => {
    if (!autoFocus) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // Runs only on mount — the editor opens focused with the caret at the end.
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `autoFocus` intentionally omitted: this is a one-time "open focused" effect, not a live sync. Adding autoFocus would re-focus and re-place the caret at the end whenever the prop's identity changes after mount, stealing focus/cursor position back from the user mid-edit. */
  }, []);
  // The backdrop and textarea must share identical text metrics (font, size,
  // padding, border width, wrapping) so the highlights sit exactly under the
  // characters. The textarea is transparent-backed; the wrapper carries the
  // visible background.
  const sharedMetrics =
    "whitespace-pre-wrap break-words px-3 py-2 text-sm leading-normal";
  return (
    <div className={cn("relative rounded-md bg-background", className)}>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 overflow-hidden rounded-md border border-transparent text-transparent",
          sharedMetrics,
        )}
      >
        {renderMergeFieldHighlights(value, flexPhrases)}
        {"​"}
      </div>
      <textarea
        ref={setRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          resize();
        }}
        onBlur={onBlur}
        onSelect={(e) => {
          if (!onTextSelect) return;
          const el = e.currentTarget;
          const text = el.value.slice(el.selectionStart, el.selectionEnd);
          if (text.trim()) onTextSelect(text);
        }}
        placeholder={placeholder}
        rows={3}
        className={cn(
          "relative w-full resize-none overflow-hidden rounded-md border border-input bg-transparent shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          sharedMetrics,
        )}
      />
    </div>
  );
}

/**
 * Skeleton preview of the table/chart the writer will generate for a section,
 * so selecting those chips shows roughly what will land in the report.
 */
function TableSkeleton({ columns }: { columns?: string[] | null }) {
  const cols = columns && columns.length > 1 ? columns : null;
  const colCount = cols?.length ?? 3;
  return (
    <div className="min-w-44 flex-1 rounded-md border border-dashed bg-muted/30 p-2">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Table — generated from the matter&apos;s data
      </div>
      <div className="overflow-hidden rounded border">
        <div
          className="grid gap-px bg-border"
          style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: colCount * 3 }, (_, i) => (
            <div
              key={i}
              className={cn(
                "flex h-5 items-center px-1.5",
                i < colCount ? "bg-muted" : "bg-background",
              )}
            >
              {i < colCount && cols ? (
                <span className="truncate text-[10px] font-medium text-muted-foreground">
                  {cols[i]}
                </span>
              ) : (
                <div
                  className={cn(
                    "h-1.5 rounded-full",
                    i < colCount
                      ? "w-3/4 bg-muted-foreground/40"
                      : "w-1/2 bg-muted-foreground/20",
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="min-w-44 flex-1 rounded-md border border-dashed bg-muted/30 p-2">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Chart — generated from the matter&apos;s data
      </div>
      <div className="flex h-[52px] items-end gap-1.5 border-b border-l border-muted-foreground/30 pl-1.5">
        {[60, 85, 40, 70, 25].map((h, i) => (
          <div
            key={i}
            className="w-4 rounded-t bg-muted-foreground/25"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function GeneratedContentPreview({
  contentTypes,
}: {
  contentTypes: ContentType[];
}) {
  const showTable = contentTypes.includes("table");
  const showChart = contentTypes.includes("chart");
  if (!showTable && !showChart) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {showTable && <TableSkeleton />}
      {showChart && <ChartSkeleton />}
    </div>
  );
}

/**
 * A single line reads as a sub-heading when it's short, unpunctuated (a
 * trailing "?" is fine — reader-guidance headings use them), and not a
 * directive. Mirrors how the section writer renders such lines in
 * exact-wording output: bold lead-ins, not <h*> headings.
 */
function isHeadingLine(line: string): boolean {
  const t = line.trim();
  return (
    t.length > 0 &&
    t.length <= 60 &&
    !t.startsWith("[") &&
    !t.startsWith("-") &&
    !/[.,;:]$/.test(t) &&
    !t.includes("{{")
  );
}

/**
 * Read-only render of a section's wording as it will roughly appear in the
 * generated report: the title styled as the report heading, short lead-in
 * lines bold, {{fields}} and [directives] as chips, and table/chart
 * directives replaced IN PLACE by skeletons (with real column names when the
 * directive lists them). Types selected but not placed by a directive get
 * their skeleton appended, so "possible" content is always visible.
 */
/**
 * Convert rich (HTML) wording from the WordingRichEditor into styled preview
 * nodes: explicit bold/italic/underline/lists are honoured, chips tinted, and
 * directive-only paragraphs replaced by in-place skeletons. The HTML is never
 * injected — React nodes are rebuilt from a whitelist of tags.
 */
function renderRichGuidance(
  html: string,
  flexPhrases: string[],
  selectedPhrases: string[] = [],
  onMergeFieldClick?: (token: string) => void,
): { nodes: ReactNode[]; sawTable: boolean; sawChart: boolean } {
  if (typeof window === "undefined") {
    return {
      nodes: [html.replace(/<[^>]+>/g, " ")],
      sawTable: isDataTableDirective(html),
      sawChart: CHART_DIRECTIVE_RE.test(html),
    };
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const nodes: ReactNode[] = [];
  let sawTable = false;
  let sawChart = false;
  let key = 0;

  const inlineNode = (node: ChildNode): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (
        <span key={key++}>
          {renderMergeFieldHighlights(
            node.textContent ?? "",
            flexPhrases,
            selectedPhrases,
            onMergeFieldClick,
          )}
        </span>
      );
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const el = node as HTMLElement;
    const tagId = el.getAttribute?.("data-morgan-data-tag");
    if (tagId) {
      const label = el.getAttribute("data-label") || el.textContent || tagId;
      return (
        <span key={key++} className={DATA_TAG_CHIP}>
          @{label.replace(/^@/, "")}
        </span>
      );
    }
    const kids = Array.from(el.childNodes).map((c) => inlineNode(c));
    switch (el.tagName) {
      case "STRONG":
      case "B":
        return <strong key={key++}>{kids}</strong>;
      case "EM":
      case "I":
        return <em key={key++}>{kids}</em>;
      case "U":
        return <u key={key++}>{kids}</u>;
      case "S":
        return <s key={key++}>{kids}</s>;
      case "BR":
        return <br key={key++} />;
      default:
        return <span key={key++}>{kids}</span>;
    }
  };
  const inlineChildren = (el: Element): ReactNode[] =>
    Array.from(el.childNodes).map((c) => inlineNode(c));

  const renderBlock = (el: Element) => {
    const tag = el.tagName;
    if (tag === "UL" || tag === "OL") {
      const items = Array.from(el.children)
        .filter((li) => li.tagName === "LI")
        .map((li) => <li key={key++}>{inlineChildren(li)}</li>);
      nodes.push(
        tag === "UL" ? (
          <ul key={key++} className="list-disc pl-5 text-sm leading-relaxed">
            {items}
          </ul>
        ) : (
          <ol key={key++} className="list-decimal pl-5 text-sm leading-relaxed">
            {items}
          </ol>
        ),
      );
      return;
    }
    // A fixed/boilerplate table transcribed from the source: render it inline,
    // in place, so the practitioner sees exactly what's in it and where it
    // sits. (Data-driven tables use a [Table …] directive → skeleton instead.)
    if (tag === "TABLE") {
      sawTable = true;
      const rows = Array.from(el.querySelectorAll("tr")).map((tr) => (
        <tr key={key++}>
          {Array.from(tr.children)
            .filter((c) => c.tagName === "TD" || c.tagName === "TH")
            .map((c) =>
              c.tagName === "TH" ? (
                <th
                  key={key++}
                  className="border px-2 py-1 text-left align-top text-sm font-semibold"
                >
                  {inlineChildren(c)}
                </th>
              ) : (
                <td
                  key={key++}
                  className="border px-2 py-1 text-left align-top text-sm"
                >
                  {inlineChildren(c)}
                </td>
              ),
            )}
        </tr>
      ));
      nodes.push(
        <div key={key++} className="overflow-x-auto rounded-md border">
          <table className="w-full border-collapse [&_th]:bg-muted">
            <tbody>{rows}</tbody>
          </table>
        </div>,
      );
      return;
    }
    const text = (el.textContent ?? "").trim();
    if (!text) {
      nodes.push(<div key={key++} className="h-2" />);
      return;
    }
    if (/^\[[^\][]+\]$/.test(text)) {
      if (isDataTableDirective(text)) {
        sawTable = true;
        nodes.push(
          <TableSkeleton key={key++} columns={parseDirectiveColumns(text)} />,
        );
        return;
      }
      if (CHART_DIRECTIVE_RE.test(text)) {
        sawChart = true;
        nodes.push(<ChartSkeleton key={key++} />);
        return;
      }
      nodes.push(
        <p key={key++} className="text-xs italic text-muted-foreground">
          {text}
        </p>,
      );
      return;
    }
    nodes.push(
      <p key={key++} className="text-sm leading-relaxed">
        {inlineChildren(el)}
      </p>,
    );
  };
  Array.from(doc.body.children).forEach(renderBlock);
  return { nodes, sawTable, sawChart };
}

function SectionWordingPreview({
  title,
  guidance,
  contentTypes,
  nested,
  flexPhrases = [],
  selectedPhrases = [],
  onMergeFieldClick,
}: {
  title: string;
  guidance: string;
  contentTypes: ContentType[];
  nested: boolean;
  /** Adaptation-note phrases to tint violet (adaptable sections). */
  flexPhrases?: string[];
  /** Assistant-tagged passages — persistently marked in primary tint. */
  selectedPhrases?: string[];
  /** Click an amber merge field to insert it into the adaptation notes. */
  onMergeFieldClick?: (token: string) => void;
}) {
  const nodes: ReactNode[] = [];
  let sawTable = false;
  let sawChart = false;
  let key = 0;

  const renderTextSegment = (segment: string) => {
    for (const raw of segment.split("\n")) {
      const line = raw.trim();
      if (!line) {
        nodes.push(<div key={key++} className="h-2" />);
        continue;
      }
      // Heading-styled lines still run through the highlighter so quoted
      // adaptation phrases and merge fields tint inside them too.
      nodes.push(
        <p key={key++} className="text-sm leading-relaxed">
          {isHeadingLine(line) ? (
            <span className="font-semibold">
              {renderMergeFieldHighlights(
                line,
                flexPhrases,
                selectedPhrases,
                onMergeFieldClick,
              )}
            </span>
          ) : (
            renderMergeFieldHighlights(
              line,
              flexPhrases,
              selectedPhrases,
              onMergeFieldClick,
            )
          )}
        </p>,
      );
    }
  };

  if (looksLikeHtml(guidance)) {
    // Rich wording from the editor: formatting is explicit, no heuristics.
    const rich = renderRichGuidance(
      guidance,
      flexPhrases,
      selectedPhrases,
      onMergeFieldClick,
    );
    nodes.push(...rich.nodes);
    sawTable = rich.sawTable;
    sawChart = rich.sawChart;
  } else {
    // Legacy plain text: split on [directives] wherever they sit —
    // standalone, mid-paragraph, or wrapped — so tables/charts land in place.
    for (const segment of guidance.split(/(\[[^\][]+\])/g)) {
      if (!segment) continue;
      if (/^\[[^\][]+\]$/.test(segment)) {
        if (isDataTableDirective(segment)) {
          sawTable = true;
          nodes.push(
            <TableSkeleton
              key={key++}
              columns={parseDirectiveColumns(segment)}
            />,
          );
        } else if (CHART_DIRECTIVE_RE.test(segment)) {
          sawChart = true;
          nodes.push(<ChartSkeleton key={key++} />);
        } else {
          nodes.push(
            <p key={key++} className="text-xs italic text-muted-foreground">
              {segment}
            </p>,
          );
        }
        continue;
      }
      renderTextSegment(segment);
    }
  }

  const pendingTable = contentTypes.includes("table") && !sawTable;
  const pendingChart = contentTypes.includes("chart") && !sawChart;

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/20 px-4 py-3">
      <div
        className={cn(
          "font-semibold text-foreground",
          nested ? "text-base" : "text-lg",
        )}
      >
        {title}
      </div>
      {guidance.trim() ? (
        nodes
      ) : (
        <p className="text-sm italic text-muted-foreground">
          No wording yet — click to add some.
        </p>
      )}
      {(pendingTable || pendingChart) && (
        <div className="flex flex-wrap gap-2 pt-1">
          {pendingTable && <TableSkeleton />}
          {pendingChart && <ChartSkeleton />}
        </div>
      )}
    </div>
  );
}

/**
 * Styled view of an AI-drafted section: unmistakably an instruction prompt,
 * not report wording — dashed border, a "drafting instructions" label, and
 * muted italic text.
 */
function FlexibleInstructionCard({
  guidance,
  contentTypes,
  selectedPhrases = [],
}: {
  guidance: string;
  contentTypes: ContentType[];
  /** Assistant-tagged passages — persistently marked in primary tint. */
  selectedPhrases?: string[];
}) {
  return (
    <div className="rounded-md border border-dashed border-primary/40 bg-primary/[0.04] px-4 py-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
        <Sparkles className="size-3" />
        Drafting instructions for Morgan — not report wording
      </div>
      {guidance.trim() ? (
        looksLikeHtml(guidance) ? (
          <div className="space-y-1.5 text-foreground">
            {renderRichGuidance(guidance, [], selectedPhrases).nodes}
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {renderMergeFieldHighlights(guidance, [], selectedPhrases)}
          </p>
        )
      ) : (
        <p className="text-sm italic text-muted-foreground">
          No instructions yet — click to describe what this section should
          contain, and @-tag the data Morgan should use.
        </p>
      )}
      <div className="mt-2">
        <GeneratedContentPreview contentTypes={contentTypes} />
      </div>
    </div>
  );
}

/**
 * Contents rail down the left of the template outline, mirroring the
 * generated report's table of contents. Clicking an entry expands that
 * section's card and scrolls it into view; the entry for the card currently
 * at the top of the viewport stays highlighted. Collapses to a narrow rail.
 */
function TemplateContents({
  entries,
  open,
  onToggle,
  onNavigate,
}: {
  entries: ContentsEntry[];
  open: boolean;
  onToggle: () => void;
  onNavigate: (id: string) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Highlight whichever section card is currently at the top of the viewport.
  // The scroll container differs by layout, so listen in the capture phase.
  useEffect(() => {
    if (!open || entries.length === 0) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      const current = Array.from(
        document.querySelectorAll<HTMLElement>("[data-template-section]"),
      ).reduce<string | null>(
        (acc, el) =>
          el.getBoundingClientRect().top <= 120
            ? (el.dataset.templateSection ?? null)
            : acc,
        null,
      );
      setActiveId(current ?? entries[0]?.id ?? null);
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [open, entries]);

  if (entries.length === 0) return null;

  if (!open) {
    return (
      <div className="sticky top-20 flex shrink-0 flex-col items-center rounded-xl border bg-background px-1 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label="Show contents"
          title="Show contents"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ListTree className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="sticky top-20 flex max-h-[calc(100vh-9rem)] w-[220px] shrink-0 flex-col overflow-hidden rounded-xl border bg-background">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <ListTree className="size-4 text-muted-foreground" />
          Contents
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Hide contents"
          title="Hide contents"
          className="text-muted-foreground hover:text-foreground"
        >
          <PanelLeftClose className="size-4" />
        </button>
      </div>
      <nav className="min-h-0 flex-1 overflow-auto py-2">
        {entries.map((e) => {
          const active = activeId === e.id;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => {
                setActiveId(e.id);
                onNavigate(e.id);
              }}
              className={cn(
                "block w-full border-l-2 py-1.5 pr-3 text-left text-xs leading-snug transition-colors",
                e.nested ? "pl-7" : "pl-4",
                active
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground",
              )}
            >
              {e.title}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export function ReportTemplateEditor({
  templateId,
}: {
  templateId: Id<"reportTemplates">;
}) {
  const { data: template, isLoading } = useQuery(
    convexQuery(api.settings.reports.queries.getReportTemplate, { templateId }),
  );

  const { mutateAsync: retryExtraction, isPending: retrying } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.retryExtraction,
    ),
  });
  const { mutateAsync: addSection } = useMutation({
    mutationFn: useConvexMutation(api.settings.reports.mutations.addSection),
  });
  const { mutateAsync: reorderSections } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.reorderSections,
    ),
  });
  const { mutateAsync: ensureTemplateWording } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.ensureTemplateWording,
    ),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Per-card collapse state (session-local; cards start expanded).
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const toggleCollapsed = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [tocOpen, setTocOpen] = useState(true);

  // Assistant chat panel + the passages the user highlighted in wording
  // boxes. Selections persist (and stay marked in the wording) until removed
  // or sent; several can be collected across sections.
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSelections, setChatSelections] = useState<TemplateChatSelection[]>(
    [],
  );
  const handleTextSelect = useCallback(
    (sectionTitle: string, text: string) => {
      // Only capture while the assistant is open — a chip materialising in a
      // closed panel would surprise on the next send.
      if (!chatOpen) return;
      const excerpt = text.trim().slice(0, 2000);
      if (excerpt.length < 4) return;
      setChatSelections((prev) => {
        if (
          prev.some(
            (s) => s.sectionTitle === sectionTitle && s.excerpt === excerpt,
          )
        )
          return prev;
        return [...prev.slice(-7), { sectionTitle, excerpt }];
      });
    },
    [chatOpen],
  );

  // Group sections into top-level + their children, preserving order.
  const { topLevel, childrenByParent } = useMemo(
    () => groupSectionsIntoTree((template?.sections ?? []) as Section[]),
    [template?.sections],
  );

  // Backfill the two wording slots for legacy templates so toggling between
  // Exact / Adaptable and AI drafted is instant. Freshly-scanned templates
  // already have both slots, so this is a one-time no-op for them.
  const needsWordingFill =
    template?.extractionStatus === "completed" &&
    (template.sections ?? []).some((s) =>
      needsTemplateWordingFill({
        fillMode: s.fillMode,
        adherence: s.adherence,
        guidance: s.guidance,
        sourceWording: s.sourceWording,
        draftingInstructions: s.draftingInstructions,
        sourceWordingStatus: s.sourceWordingStatus,
      }),
    );

  useEffect(() => {
    if (!needsWordingFill) return;
    void ensureTemplateWording({ templateId }).catch((err: unknown) => {
      toast.error(
        extractErrorMessage(
          err,
          "Failed to prepare wording from the uploaded report",
        ),
      );
    });
  }, [needsWordingFill, templateId, ensureTemplateWording]);

  const handleReorder = useCallback(
    async (orderedIds: Id<"reportTemplateSections">[]) => {
      try {
        await reorderSections({ templateId, orderedIds });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to reorder"));
      }
    },
    [reorderSections, templateId],
  );

  const handleTopLevelDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const reordered = computeReorderedIds(
        topLevel.map((s) => s._id),
        active.id as Id<"reportTemplateSections">,
        over?.id as Id<"reportTemplateSections"> | undefined,
      );
      if (reordered) handleReorder(reordered);
    },
    [topLevel, handleReorder],
  );

  // Contents-rail entries: top-level sections with their children indented.
  const contentsEntries = useMemo<ContentsEntry[]>(
    () => buildContentsEntries({ topLevel, childrenByParent }),
    [topLevel, childrenByParent],
  );

  const navigateToSection = useCallback(
    (id: string) => {
      // Expand the target card (and its parent, since sub-section cards live
      // inside the parent's body), then scroll once the layout has settled.
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        const target = ((template?.sections ?? []) as Section[]).find(
          (s) => s._id === id,
        );
        if (target?.parentSectionId) next.delete(target.parentSectionId);
        return next;
      });
      requestAnimationFrame(() => {
        document
          .querySelector<HTMLElement>(`[data-template-section="${id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [template?.sections],
  );

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!template) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Template not found.
      </div>
    );
  }

  const status = template.extractionStatus as
    "pending" | "extracting" | "completed" | "failed" | undefined;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-3">
        {/* Toolbar spans both columns so the rail and cards start level. */}
        <div className="flex items-center justify-end gap-3">
          {topLevel.length > 1 && (
            <>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setCollapsedIds(new Set())}
              >
                Expand all
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() =>
                  setCollapsedIds(
                    new Set(
                      ((template?.sections ?? []) as Section[]).map(
                        (s) => s._id as string,
                      ),
                    ),
                  )
                }
              >
                Collapse all
              </button>
            </>
          )}
          <Button
            variant={chatOpen ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setChatOpen((v) => !v);
              if (chatOpen) setChatSelections([]);
            }}
          >
            <MessageSquare className="mr-1 size-3.5" />
            Assistant
          </Button>
        </div>

        <div className="flex items-start gap-4">
          <TemplateContents
            entries={contentsEntries}
            open={tocOpen}
            onToggle={() => setTocOpen((v) => !v)}
            onNavigate={navigateToSection}
          />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {(status === "pending" || status === "extracting") && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                <Loader2 className="size-4 animate-spin" />
                Scanning the report and building the section outline…
              </div>
            )}
            {status === "failed" && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <span className="flex items-center gap-2">
                  <AlertCircle className="size-4" />
                  {template.extractionError ?? "The report scan failed."}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retrying}
                  onClick={async () => {
                    try {
                      await retryExtraction({ templateId });
                      toast.success("Re-scanning the report…");
                    } catch (err) {
                      toast.error(extractErrorMessage(err, "Failed to retry"));
                    }
                  }}
                >
                  <RefreshCw className="mr-1 size-3.5" />
                  Retry
                </Button>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {topLevel.length === 0 && status === "completed" && (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No sections yet. Add one below.
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTopLevelDragEnd}
              >
                <SortableContext
                  items={topLevel.map((s) => s._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-3">
                    {topLevel.map((section) => (
                      <SortableSection key={section._id} id={section._id}>
                        {(handleProps) => (
                          <SectionCard
                            section={section}
                            templateId={templateId}
                            childSections={
                              childrenByParent.get(section._id) ?? []
                            }
                            onReorder={handleReorder}
                            dragHandle={handleProps}
                            collapsedIds={collapsedIds}
                            onToggleCollapsed={toggleCollapsed}
                            onTextSelect={handleTextSelect}
                          />
                        )}
                      </SortableSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await addSection({ templateId, title: "New section" });
                  } catch (err) {
                    toast.error(
                      extractErrorMessage(err, "Failed to add section"),
                    );
                  }
                }}
              >
                <Plus className="mr-1 size-3.5" />
                Add section
              </Button>
            </div>
          </div>

          {chatOpen && (
            <TemplateChatPanel
              templateId={templateId}
              sectionTitles={contentsEntries.map((e) => e.title)}
              selections={chatSelections}
              onRemoveSelection={(index) =>
                setChatSelections((prev) => prev.filter((_, i) => i !== index))
              }
              onClearSelections={() => setChatSelections([])}
              onClose={() => {
                setChatOpen(false);
                setChatSelections([]);
              }}
            />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Drag handle props passed down so the card can render a grip. */
type DragHandleProps = {
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
};

/** Wraps a child in a sortable container and exposes drag-handle props. */
function SortableSection({
  id,
  children,
}: {
  id: Id<"reportTemplateSections">;
  children: (handle: DragHandleProps) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function SectionCard({
  section,
  templateId,
  childSections,
  onReorder,
  dragHandle,
  nested = false,
  collapsedIds,
  onToggleCollapsed,
  onTextSelect,
  chatSelections = [],
}: {
  section: Section;
  templateId: Id<"reportTemplates">;
  childSections: Section[];
  onReorder: (orderedIds: Id<"reportTemplateSections">[]) => void;
  dragHandle: DragHandleProps;
  nested?: boolean;
  collapsedIds: ReadonlySet<string>;
  onToggleCollapsed: (id: string) => void;
  /** Bubbles a highlighted wording passage up to the assistant chat. */
  onTextSelect?: (sectionTitle: string, text: string) => void;
  /** Passages tagged for the assistant — marked in this section's wording. */
  chatSelections?: TemplateChatSelection[];
}) {
  const { mutateAsync: updateSection } = useMutation({
    mutationFn: useConvexMutation(api.settings.reports.mutations.updateSection),
  });
  const { mutateAsync: retryTemplateWording } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.reports.mutations.ensureTemplateWording,
    ),
  });
  const { mutateAsync: deleteSection } = useMutation({
    mutationFn: useConvexMutation(api.settings.reports.mutations.deleteSection),
  });
  const { mutateAsync: addSection } = useMutation({
    mutationFn: useConvexMutation(api.settings.reports.mutations.addSection),
  });

  const [title, setTitle] = useState(section.title);
  const [guidance, setGuidance] = useState(section.guidance ?? "");
  const [adaptationNotes, setAdaptationNotes] = useState(
    section.adaptationNotes ?? "",
  );
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  // Keep local buffers in sync if the underlying row changes.
  useEffect(() => setTitle(section.title), [section.title]);
  useEffect(() => setGuidance(section.guidance ?? ""), [section.guidance]);
  useEffect(
    () => setAdaptationNotes(section.adaptationNotes ?? ""),
    [section.adaptationNotes],
  );

  const isAttachment = section.fillMode === "attachment";
  // Effective adherence: honours the stored field, falling back to detection
  // of FIXED-SECTION-style guidance written before the field existed.
  const adherence = resolveAdherence(section);
  const isStrict = adherence === "strict";
  const isAdaptable = adherence === "adaptable";
  const adherenceMeta = ADHERENCE_META[adherence];
  const AdherenceIcon = adherenceMeta.icon;
  const collapsed = collapsedIds.has(section._id);
  // Cards show the styled render by default; clicking it (or the pencil)
  // opens the plain-text editor, and blur returns to the styled view.
  const [editing, setEditing] = useState(false);
  // In edit mode the bottom skeletons only stand in for table/chart types the
  // wording doesn't already place with a [directive].
  const unplacedTypes = section.contentTypes.filter(
    (ct) =>
      (ct !== "table" || !isDataTableDirective(guidance)) &&
      (ct !== "chart" || !CHART_DIRECTIVE_RE.test(guidance)),
  );
  // Phrases the adaptation notes flag to flex — tinted violet in the wording.
  const flexPhrases = useMemo(
    () => (isAdaptable ? extractQuotedPhrases(adaptationNotes) : []),
    [isAdaptable, adaptationNotes],
  );
  // Assistant-tagged passages in THIS section — kept persistently marked.
  const selectedPhrases = useMemo(
    () =>
      chatSelections
        .filter((sel) => sel.sectionTitle === section.title)
        .map((sel) => sel.excerpt),
    [chatSelections, section.title],
  );

  // Styled view: a mouse-up with a selection tags the passage for the
  // assistant; a plain click opens the editor.
  const handleStyledMouseUp = useCallback(() => {
    const sel = window.getSelection()?.toString() ?? "";
    if (sel.trim().length >= 4) {
      onTextSelect?.(section.title, sel);
      return;
    }
    setEditing(true);
  }, [onTextSelect, section.title]);

  const childSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const commitTitle = useCallback(async () => {
    const trimmed = title.trim();
    if (!trimmed || trimmed === section.title) {
      setTitle(section.title);
      return;
    }
    try {
      await updateSection({ sectionId: section._id, title: trimmed });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename section"));
      setTitle(section.title);
    }
  }, [title, section._id, section.title, updateSection]);

  const commitAdaptationNotes = useCallback(async () => {
    if (adaptationNotes === (section.adaptationNotes ?? "")) return;
    try {
      await updateSection({ sectionId: section._id, adaptationNotes });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to save adaptation notes"));
    }
  }, [adaptationNotes, section._id, section.adaptationNotes, updateSection]);

  /**
   * Drop a clicked `{{merge field}}` into the adaptation notes at the caret
   * (or at the end if the notes aren't focused), then persist.
   */
  const insertMergeField = useCallback(
    (token: string) => {
      const el = notesRef.current;
      const current = adaptationNotes;
      const focused = !!el && document.activeElement === el;
      const start = focused && el ? el.selectionStart : current.length;
      const end = focused && el ? el.selectionEnd : current.length;
      const before = current.slice(0, start);
      const after = current.slice(end);
      const pad = before.length > 0 && !/\s$/.test(before) ? " " : "";
      const next = before + pad + token + after;
      const caret = start + pad.length + token.length;
      setAdaptationNotes(next);
      if (next !== (section.adaptationNotes ?? "")) {
        updateSection({ sectionId: section._id, adaptationNotes: next }).catch(
          (err) =>
            toast.error(
              extractErrorMessage(err, "Failed to save adaptation notes"),
            ),
        );
      }
      requestAnimationFrame(() => {
        const ta = notesRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(caret, caret);
      });
    },
    [adaptationNotes, section._id, section.adaptationNotes, updateSection],
  );

  const toggleContentType = useCallback(
    async (ct: ContentType) => {
      const set = new Set(section.contentTypes);
      if (set.has(ct)) set.delete(ct);
      else set.add(ct);
      const next = Array.from(set);
      if (next.length === 0) next.push("narrative");
      try {
        await updateSection({ sectionId: section._id, contentTypes: next });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to update content types"));
      }
    },
    [section.contentTypes, section._id, updateSection],
  );

  const setFillMode = useCallback(
    async (mode: FillMode) => {
      if (mode === section.fillMode) return;
      try {
        await updateSection({ sectionId: section._id, fillMode: mode });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to update fill mode"));
      }
    },
    [section.fillMode, section._id, updateSection],
  );

  const setAdherence = useCallback(
    async (mode: SectionAdherence) => {
      if (mode === resolveAdherence(section)) return;
      try {
        await updateSection({ sectionId: section._id, adherence: mode });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to update adherence"));
      }
    },
    [section, updateSection],
  );

  const handleChildDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const reordered = computeReorderedIds(
        childSections.map((s) => s._id),
        active.id as Id<"reportTemplateSections">,
        over?.id as Id<"reportTemplateSections"> | undefined,
      );
      if (reordered) onReorder(reordered);
    },
    [childSections, onReorder],
  );

  return (
    <div
      data-template-section={section._id}
      className={cn(
        "scroll-mt-4 rounded-lg border bg-background p-3",
        isAttachment && "border-amber-200 bg-amber-50/40",
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="mt-1.5 cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...dragHandle.attributes}
          {...dragHandle.listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <button
          type="button"
          className="mt-1.5 text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          onClick={() => onToggleCollapsed(section._id)}
        >
          {collapsed ? (
            <ChevronRight className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </button>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            {collapsed ? (
              <button
                type="button"
                className="h-8 min-w-0 flex-1 truncate text-left text-sm font-medium"
                onClick={() => onToggleCollapsed(section._id)}
              >
                {section.title}
              </button>
            ) : (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={commitTitle}
                className="h-8 font-medium"
              />
            )}
            {collapsed && !isAttachment && (
              <span
                className="flex shrink-0 items-center gap-1.5 text-muted-foreground"
                title={section.contentTypes.join(", ")}
              >
                {CONTENT_TYPE_OPTIONS.filter((o) =>
                  section.contentTypes.includes(o.value),
                ).map((o) => (
                  <o.icon key={o.value} className="size-3.5" />
                ))}
              </span>
            )}
            {!isAttachment && (
              <Badge
                variant="outline"
                className="shrink-0 font-normal text-muted-foreground"
              >
                <AdherenceIcon className="mr-1 size-3" />
                {adherenceMeta.label}
              </Badge>
            )}
            {isAttachment && (
              <Badge color="amber" className="shrink-0">
                <Paperclip className="mr-1 size-3" />
                Attachment
              </Badge>
            )}
            {!collapsed &&
              !isAttachment &&
              !editing &&
              section.sourceWordingStatus !== "extracting" && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-8 shrink-0 text-muted-foreground"
                  onClick={() => setEditing(true)}
                  aria-label="Edit wording"
                  title="Edit this section's text (blur to return to the styled view)"
                >
                  <PenLine className="size-4" />
                </Button>
              )}
            <Button
              size="icon"
              variant="ghost"
              className="size-8 shrink-0 text-muted-foreground hover:text-red-600"
              onClick={async () => {
                try {
                  await deleteSection({ sectionId: section._id });
                } catch (err) {
                  toast.error(extractErrorMessage(err, "Failed to delete"));
                }
              }}
              aria-label="Delete section"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>

          {!collapsed &&
            !isAttachment &&
            !editing &&
            (section.sourceWordingStatus === "extracting" ||
            section.sourceWordingStatus === "failed" ? (
              section.sourceWordingStatus === "extracting" ? (
                <div className="flex items-center gap-2 rounded-md border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                  <Spinner className="size-4" />
                  Pulling this section&apos;s wording from the uploaded report…
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground">
                  <AlertCircle className="size-4 text-destructive" />
                  Couldn&apos;t pull wording from the uploaded report.
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() => {
                      void retryTemplateWording({
                        templateId,
                        retryFailed: true,
                      }).catch((err: unknown) =>
                        toast.error(
                          extractErrorMessage(
                            err,
                            "Failed to retry wording pull",
                          ),
                        ),
                      );
                    }}
                  >
                    Retry
                  </Button>
                </div>
              )
            ) : (
              <div
                className="cursor-text"
                onMouseUp={handleStyledMouseUp}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setEditing(true);
                }}
              >
                {adherence === "flexible" ? (
                  <FlexibleInstructionCard
                    guidance={guidance}
                    contentTypes={section.contentTypes}
                    selectedPhrases={selectedPhrases}
                  />
                ) : (
                  <SectionWordingPreview
                    title={section.title}
                    guidance={guidance}
                    contentTypes={section.contentTypes}
                    nested={nested}
                    flexPhrases={flexPhrases}
                    selectedPhrases={selectedPhrases}
                    onMergeFieldClick={
                      isAdaptable ? insertMergeField : undefined
                    }
                  />
                )}
              </div>
            ))}

          {!collapsed && !isAttachment && editing && (
            <div className="space-y-1">
              {adherence === "flexible" && (
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                  <Sparkles className="size-3" />
                  Drafting instructions for Morgan — not report wording
                </div>
              )}
              <WordingRichEditor
                value={guidance}
                onCommit={(html) => {
                  setGuidance(html);
                  setEditing(false);
                  if (html !== (section.guidance ?? "")) {
                    updateSection({
                      sectionId: section._id,
                      guidance: html,
                      dataTagIds: extractDataTagIds(html),
                    }).catch((err) =>
                      toast.error(
                        extractErrorMessage(err, "Failed to save wording"),
                      ),
                    );
                  }
                }}
                onTextSelect={
                  onTextSelect
                    ? (text) => onTextSelect(section.title, text)
                    : undefined
                }
                flexPhrases={flexPhrases}
                selectedPhrases={selectedPhrases}
                placeholder={
                  isStrict || isAdaptable
                    ? "Paste the section's standard wording. Use {{field}} for values that change per matter, and type @ to tag a data source."
                    : "Describe what this section should contain and cover; Morgan drafts it from the matter's data. Type @ to tag a data source."
                }
              />
            </div>
          )}

          {!collapsed && !isAttachment && isAdaptable && (
            <div className="space-y-1">
              <div className="rounded-md border border-violet-200/80 bg-violet-50/70 px-3 py-2 text-[11px] leading-snug text-violet-950 dark:border-violet-800/60 dark:bg-violet-400/10 dark:text-violet-100">
                <p className="font-medium">How to adapt language</p>
                <p className="mt-1">
                  Click on a field from above (e.g.{" "}
                  <span
                    className={cn(
                      MERGE_FIELD_CHIP,
                      "px-1 text-[10px] font-medium",
                    )}
                  >
                    {"{{Liquidator name}}"}
                  </span>
                  ) to adapt wording. Use &quot;quotation&quot; marks to also
                  select words or phrases to reference and adapt.
                </p>
              </div>
              <AutoTextarea
                value={adaptationNotes}
                onChange={setAdaptationNotes}
                onBlur={commitAdaptationNotes}
                textareaRef={notesRef}
                placeholder={
                  'e.g. Adapt any references from "liquidations" to the appointment type.'
                }
              />
              {/* Live feedback: which quoted phrases were found in the wording. */}
              {flexPhrases.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pt-0.5 text-xs">
                  {flexPhrases.map((p, i) => {
                    const found = guidance
                      .toLowerCase()
                      .includes(p.toLowerCase());
                    return (
                      <span
                        key={i}
                        className={cn(
                          "rounded-[4px] px-1.5 py-0.5",
                          found
                            ? FLEX_PHRASE_PILL
                            : "border border-dashed border-muted-foreground/40 text-muted-foreground line-through",
                        )}
                        title={
                          found
                            ? "Found in the wording — underlined in violet above"
                            : "Not found in the wording — check the exact words match"
                        }
                      >
                        {p}
                      </span>
                    );
                  })}
                  {flexPhrases.some(
                    (p) => !guidance.toLowerCase().includes(p.toLowerCase()),
                  ) && (
                    <span className="text-muted-foreground">
                      struck phrases weren&apos;t found in the wording — the
                      quotes must match it word-for-word
                    </span>
                  )}
                </div>
              )}
              {flexPhrases.length === 0 &&
                adaptationNotes.trim().length > 0 && (
                  <div className="pt-0.5 text-xs text-muted-foreground">
                    Tip: no quoted phrases yet — put the exact words in
                    &quot;quotes&quot; to see them highlighted in the wording.
                  </div>
                )}
            </div>
          )}

          {!collapsed && !isAttachment && editing && (
            <GeneratedContentPreview contentTypes={unplacedTypes} />
          )}

          {!collapsed && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-md border text-xs">
                <PillTip tip="Morgan drafts this section whenever a report is generated from this template.">
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-1 px-2.5 py-1",
                      !isAttachment
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-muted",
                    )}
                    onClick={() => setFillMode("generate")}
                  >
                    <PenLine className="size-3" />
                    Generate
                  </button>
                </PillTip>
                <PillTip tip="Nothing is drafted — this section is carried as a standard annexure/attachment.">
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-1 px-2.5 py-1",
                      isAttachment
                        ? "bg-amber-500 text-white"
                        : "bg-background text-muted-foreground hover:bg-muted",
                    )}
                    onClick={() => setFillMode("attachment")}
                  >
                    <Paperclip className="size-3" />
                    Attachment only
                  </button>
                </PillTip>
              </div>

              {!isAttachment && (
                <div className="inline-flex overflow-hidden rounded-md border text-xs">
                  <PillTip tip="Morgan drafts this section for each matter from the case data, guided by the brief you write here. Toggle to Exact or Adaptable to work from the report's own wording instead.">
                    <button
                      type="button"
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1",
                        adherence === "flexible"
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted",
                      )}
                      onClick={() => setAdherence("flexible")}
                    >
                      <Sparkles className="size-3" />
                      AI drafted
                    </button>
                  </PillTip>
                  <PillTip tip='Morgan keeps this wording, but may adapt grammar and repetition where your adaptation notes allow — e.g. two directors, or a paragraph repeated per asset. Quote the exact words in the notes (e.g. adapt "the director has") and they highlight violet.'>
                    <button
                      type="button"
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1",
                        isAdaptable
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted",
                      )}
                      onClick={() => setAdherence("adaptable")}
                    >
                      <SlidersHorizontal className="size-3" />
                      Adaptable wording
                    </button>
                  </PillTip>
                  <PillTip tip="Morgan reproduces this wording word-for-word in every report, filling only the {{merge fields}} from the matter's data.">
                    <button
                      type="button"
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1",
                        isStrict
                          ? "bg-primary text-primary-foreground"
                          : "bg-background text-muted-foreground hover:bg-muted",
                      )}
                      onClick={() => setAdherence("strict")}
                    >
                      <Lock className="size-3" />
                      Exact wording
                    </button>
                  </PillTip>
                </div>
              )}

              <div className="flex flex-wrap gap-1">
                {CONTENT_TYPE_OPTIONS.map((opt) => {
                  const active = section.contentTypes.includes(opt.value);
                  return (
                    <PillTip key={opt.value} tip={CONTENT_TYPE_TIPS[opt.value]}>
                      <button
                        type="button"
                        className={cn(
                          "flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                          active
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground hover:bg-muted",
                        )}
                        onClick={() => toggleContentType(opt.value)}
                      >
                        <opt.icon className="size-3" />
                        {opt.label}
                      </button>
                    </PillTip>
                  );
                })}
              </div>
            </div>
          )}

          {/* Child sub-sections (themselves drag-sortable) */}
          {!collapsed && childSections.length > 0 && (
            <div className="mt-2">
              <DndContext
                sensors={childSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleChildDragEnd}
              >
                <SortableContext
                  items={childSections.map((s) => s._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="ml-4 flex flex-col gap-2 border-l pl-3">
                    {childSections.map((child) => (
                      <SortableSection key={child._id} id={child._id}>
                        {(handleProps) => (
                          <SectionCard
                            section={child}
                            templateId={templateId}
                            childSections={[]}
                            onReorder={onReorder}
                            dragHandle={handleProps}
                            nested
                            collapsedIds={collapsedIds}
                            onToggleCollapsed={onToggleCollapsed}
                            onTextSelect={onTextSelect}
                            chatSelections={chatSelections}
                          />
                        )}
                      </SortableSection>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}

          {!collapsed && !nested && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={async () => {
                try {
                  await addSection({
                    templateId,
                    title: "New sub-section",
                    parentSectionId: section._id,
                  });
                } catch (err) {
                  toast.error(extractErrorMessage(err, "Failed to add"));
                }
              }}
            >
              + Add sub-section
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
